// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { LinkTypes, I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Misc
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import ChromeStoreRating from '../components/localized/ChromeStoreRating'
import Testimonials from '../components/localized/Testimonials'

import { FlexHeroSection, FlexItem as HeroFlexItem, Title as HeroTitle, Description as HeroDescription } from '../components/Hero'
import { CenteredSection, Title as CenteredTitle } from '../components/CenteredSection'
import ColoredBackground from '../components/ColoredBackground'

import PrimaryAction from '../components/PrimaryAction'
import Highlight from '../components/Highlight'
import FeatureGrid, { FeatureBlock } from '../components/FeatureGrid'

import BottomCta from '../components/BottomCta'

import HeroImage from '../../public/assets/feature-images/en/sidebar.svg'

import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'
/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'plagiarism' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, plagiarism: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const HERO_BACKGROUND = 'green'

const HeroImageWrapper = styled.div`
  margin: 0 auto;
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type PlagiarismKey = I18nextKey<typeof import ('../../public/locales/en/plagiarism.json')>
type FeatureBlockContent = (
  Record<'titleKey' | 'descriptionKey', PlagiarismKey> &
  Omit<FeatureBlock, 'title' | 'description'> &
  Partial<LinkTypes> // IDK why but typescript does not find the 'linkTypes' in FeatureBlock?
)

/**
 * Mapping json keys for content structure
 */

const FEATURES: FeatureBlockContent[] = [
  {
    titleKey: 'features.upload.TITLE',
    descriptionKey: 'features.upload.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/upload.svg'), alt: '' }
  },
  {
    titleKey: 'features.run.TITLE',
    descriptionKey: 'features.run.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/performance.svg'), alt: '' }
  },
  {
    titleKey: 'features.similarity.TITLE',
    descriptionKey: 'features.similarity.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/globe.svg'), alt: '' }
  }
]

const FAQ: FeatureBlockContent[] = [
  {
    titleKey: 'faq.plagiarism.QUESTION',
    descriptionKey: 'faq.plagiarism.ANSWER'
  },
  {
    titleKey: 'faq.avoid.QUESTION',
    descriptionKey: 'faq.avoid.ANSWER'
  },
  {
    titleKey: 'faq.pricing.QUESTION',
    descriptionKey: 'faq.pricing.ANSWER'
  },
  {
    titleKey: 'faq.checkCount.QUESTION',
    descriptionKey: 'faq.checkCount.ANSWER'
  }
]

const Plagiarism: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('plagiarism')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />
      <FAQPageJsonLd
        mainEntity={[
          { questionName: t('seoFaq.installation.QUESTION'), acceptedAnswerText: t('seoFaq.installation.ANSWER') },
          { questionName: t('seoFaq.pricing.QUESTION'), acceptedAnswerText: t('seoFaq.pricing.ANSWER') }
        ]}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <FlexHeroSection>
          <HeroFlexItem>
            <HeroTitle>{t('hero.TITLE')}</HeroTitle>
            <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
            <div>
              <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
              <ChromeStoreRating t={tCommon} />
            </div>
          </HeroFlexItem>
          <HeroFlexItem>
            <HeroImageWrapper><Image src={HeroImage} alt='' priority /></HeroImageWrapper>
          </HeroFlexItem>
        </FlexHeroSection>
      </ColoredBackground>

      {/* Features */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey={'features.TITLE'}><Highlight color='purple'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          list={FEATURES.map(({ titleKey, descriptionKey, ...rest }) => ({
            ...rest,
            title: t(titleKey) as string,
            description: t(descriptionKey) as string
          }))}
        />
      </CenteredSection>

      {/* FAQ */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey={'faq.TITLE'}><Highlight color='green'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          cols={2}
          descVisibleOnMobile
          list={FAQ.map(({ titleKey, descriptionKey, ...rest }) => ({
            ...rest,
            title: t(titleKey) as string,
            description: t(descriptionKey) as string
          }))}
        />
      </CenteredSection>

      {/* Testimonials */}
      <Testimonials t={tCommon} />

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Plagiarism
